import * as Yup from 'yup';

import { TEXTS } from './constants';

import {
  SelectValue,
  Optional,
  MakeOptional,
  ARG_OR_EXT,
  ValuesOf,
} from '@/types';
import { EnumFriendsTournamentType } from '@/types/tda';
import {
  LETTERS_WITH_SPECIAL_CHARACTERS,
  ONLY_LETTERS_AND_NUMBERS,
  ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS,
  REGEX_EMAIL,
} from '@/utils/regex';

export const recoverEmailSchema = Yup.object().shape({
  docNumber: Yup.string()
    .trim()
    .min(2, TEXTS.minTwoNumbers)
    .max(11, TEXTS.docNumberValid)
    .required('Debés ingresar tu documento.')
    .test('isNotValid', TEXTS.docNumberValid, (value: string) => {
      if (value.match(/^[0-9]*$/) === null) {
        return false;
      } else {
        return true;
      }
    }),
});

export const nameTeamSchema = Yup.object({
  nameTeam: Yup.string()
    .required('Ingresá un nombre para tu equipo.')
    .min(2, TEXTS.nameTeam)
    .max(30, TEXTS.nameTeam)
    .trim(),
});

export const myTeamSchema = Yup.object().shape({
  teamName: Yup.string()
    .min(2, TEXTS.nameTeam)
    .max(30, TEXTS.nameTeam)
    .required(
      'El nombre de equipo no puede quedar vacío. ¡Elegí uno y comenzá a jugar!'
    )
    .trim(),
});

export const inviteFriendSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, TEXTS.registration.name)
    .max(30, TEXTS.registration.name)
    .matches(LETTERS_WITH_SPECIAL_CHARACTERS, TEXTS.registration.name)
    .required('Debés ingresar tu nombre.'),
  email: Yup.string()
    .email(TEXTS.emailValid)
    .matches(REGEX_EMAIL, TEXTS.emailValid)
    .required('Debés ingresar tu email.'),
});

export const nameTdaSchema = Yup.object().shape({
  tourneyName: Yup.string()
    .matches(ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS, TEXTS.tda.invalidCharacter)
    .min(2, TEXTS.tda.minName)
    .max(25, TEXTS.tda.maxName)
    .required('Debés ingresar un nombre'),
});

export const nameFCupSchema = Yup.object().shape({
  teamName: Yup.string()
    .matches(ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS, TEXTS.fCup.invalidCharacter)
    .min(2, TEXTS.fCup.minName)
    .max(25, TEXTS.fCup.maxName)
    .required('Debés ingresar un nombre'),
});

const baseCreateTda = {
  tourneyName: Yup.string()
    .matches(ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS, TEXTS.tda.invalidCharacter)
    .min(2, TEXTS.tda.minName)
    .max(25, TEXTS.tda.maxName)
    .required('Debés ingresar un nombre'),
  initDay: Yup.object().shape({
    label: Yup.string().required('La fecha de inicio es requerida'),
    value: Yup.number().required('La fecha de inicio es requerida'),
  }),
  tourneyType: Yup.number()
    .strict(true)
    .required('Debés seleccionar un tipo de torneo'),
};

const createPinSchema = () =>
  Yup.string()
    .required('Ingresá un pin.')
    .trim('Los pines no pueden contener espacios')
    .matches(ONLY_LETTERS_AND_NUMBERS, TEXTS.pins.invalid)
    .strict(true)
    .max(12, TEXTS.pins.maxlenght);

export const createCommonTdaSchema = Yup.object().shape({
  ...baseCreateTda,
});

export const createTdaWithPinsSchema = Yup.object().shape({
  ...baseCreateTda,
  pin1: Yup.string().when('tourneyType', (tourneyType, schema) => {
    return tourneyType[0] === EnumFriendsTournamentType.Vip
      ? createPinSchema()
      : schema;
  }),
  pin2: Yup.string().when('tourneyType', (tourneyType, schema) => {
    return tourneyType[0] === EnumFriendsTournamentType.Vip
      ? createPinSchema()
      : schema;
  }),
});

export const createPremiumTdaSchema = Yup.object().shape({
  ...baseCreateTda,
});

export const createFCupSchema = Yup.object().shape({
  groupName: Yup.string()
    .matches(ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS, TEXTS.fCup.invalidCharacter)
    .min(2, TEXTS.fCup.minName)
    .max(25, TEXTS.fCup.maxName)
    .required('Debés ingresar un nombre'),
});

export const createMLSchema = Yup.object().shape({
  groupName: Yup.string()
    .matches(
      ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS,
      TEXTS.minileague.invalidCharacter
    )
    .min(2, TEXTS.minileague.minName)
    .max(25, TEXTS.minileague.maxName)
    .required('Debés ingresar un nombre'),
});

export const searchTourneySchema = Yup.object().shape({
  valueSearch: Yup.string()
    .matches(
      ONLY_LETTERS_WITH_ACCENT_AND_NUMBERS,
      TEXTS.search.invalidCharacter
    )
    .min(2, TEXTS.minLetters),
});

export const myCbuSchema = Yup.object().shape({
  nameAndSurName: Yup.string()
    .min(5, TEXTS.myCbu.nameAndSurName)
    .max(30, TEXTS.myCbu.nameAndSurName)
    .matches(LETTERS_WITH_SPECIAL_CHARACTERS, TEXTS.myCbu.nameAndSurName)
    .required('Nombre y apellido del titular de la cuenta es obligatorio.'),
  cuit: Yup.string().required('CUIT es obligatorio.'),
  bank: Yup.string()
    .min(2, TEXTS.myCbu.bank)
    .max(50, TEXTS.myCbu.bank)
    .matches(
      ONLY_LETTERS_AND_NUMBERS,
      'El Banco ingresado contiene caracteres no permitidos'
    )
    .required('Banco es obligatorio.'),
  acountNumber: Yup.string()
    .min(2, TEXTS.myCbu.acountNumber)
    .max(50, TEXTS.myCbu.acountNumber)
    .required('Número de cuenta es obligatorio.'),
  cbu: Yup.string()
    .min(22, TEXTS.myCbu.cbu)
    .max(22, TEXTS.myCbu.cbu)
    .required('CBU es obligatorio.'),
});

export const editSchoolDataSchema = Yup.object().shape({
  regSchool: Yup.string()
    .min(2, 'Debes ingresar el nombre de la escuela.')
    .required('Debés ingresar el nombre de la escuela.'),
  regLocation: Yup.object().required('Debés seleccionar una localidad.'),
  regParty: Yup.object().required('Debés seleccionar un partido.'),
  regProvince: Yup.object().required('Debés seleccionar una provincia.'),
  tdeType: Yup.string().required('Debés seleccionar un nivel educativo.'),
});

export const editPersonalDataSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, TEXTS.registration.name)
    .max(30, TEXTS.registration.name)
    .matches(LETTERS_WITH_SPECIAL_CHARACTERS, TEXTS.registration.name)
    .required('Debés ingresar tu nombre.'),
  lastName: Yup.string()
    .min(2, TEXTS.registration.lastName)
    .max(30, TEXTS.registration.lastName)
    .matches(LETTERS_WITH_SPECIAL_CHARACTERS, TEXTS.registration.lastName)
    .required('Debés ingresar tu apellido.'),
  birthDate: Yup.date()
    .nullable()
    .required('Debés ingresar tu fecha de nacimiento.')
    .test('age-validation', function (value) {
      const { docArg } = this.parent; // Obtener el valor de `docArg` desde el contexto del formulario
      if (!value || !docArg) return false; // Verifica que `value` y `docArg` sean válidos

      const age = new Date().getFullYear() - value.getFullYear();
      const isArgentinian = docArg === ARG_OR_EXT.arg;

      if (isArgentinian && age < 6) {
        return this.createError({
          message: 'El usuario no tiene la edad permitida para participar.',
        });
      } else if (!isArgentinian && age < 13) {
        return this.createError({
          message: 'La edad mínima para poder participar es de 13 años.',
        });
      }
      return true; // Si pasa las validaciones, la fecha es válida
    }),
  supportedClub: Yup.object().required(
    'Debés ingresar el nombre del club del cual sos hincha.'
  ),
  customClubName: Yup.string().when(
    'supportedClub',
    (
      supportedClub: Optional<MakeOptional<SelectValue, 'value' | 'label'>[]>
    ) => {
      const supportTeam = supportedClub?.[0]?.value === -1;
      if (supportTeam) {
        return Yup.string()
          .min(2, TEXTS.clubName.length)
          .max(30, TEXTS.clubName.length)
          .required('Debés ingresar el nombre de tu equipo.');
      }

      return Yup.string()
        .min(2, TEXTS.clubName.length)
        .max(40, TEXTS.clubName.length);
    }
  ),
  areaCode: Yup.string().max(4, TEXTS.editPersonalData.areaCode),
  phoneNum: Yup.string().max(9, TEXTS.editPersonalData.phoneNumber),
  cellphoneNum: Yup.string()
    .max(9, TEXTS.editPersonalData.phoneNumber)
    .nullable()
    .test(
      'both-or-none',
      'Debés completar el número de celular para poder contactarte.',
      function (value) {
        const { cellAreaCode } = this.parent; // Accede al campo `cellAreaCode`
        const bothEmpty = !value && !cellAreaCode; // Ambos vacíos
        const bothFilled = !!value && !!cellAreaCode; // Ambos completos
        // Si no están ambos vacíos ni ambos completos, es inválido
        if (!bothEmpty && !bothFilled) {
          return false;
        }
        return true;
      }
    )
    .test(
      'min-digits',
      'El cód. de área/nro. de celular debe tener al menos 10 dígitos.',
      function (value) {
        const { cellAreaCode } = this.parent; // Accede al campo `cellAreaCode`
        const totalLength = (cellAreaCode?.length || 0) + (value?.length || 0);
        // Valida longitud solo si ambos campos están completos
        if (value && cellAreaCode && totalLength < 10) {
          return false;
        }
        return true;
      }
    ),
  cellAreaCode: Yup.string()
    .max(4, TEXTS.editPersonalData.areaCode)
    .nullable()
    .test(
      'both-or-none',
      'Debés completar el número de celular para poder contactarte.',
      function (value) {
        const { cellphoneNum } = this.parent; // Accede al campo `cellphoneNum`
        const bothEmpty = !value && !cellphoneNum; // Ambos vacíos
        const bothFilled = !!value && !!cellphoneNum; // Ambos completos
        // Si no están ambos vacíos ni ambos completos, es inválido
        if (!bothEmpty && !bothFilled) {
          return false;
        }
        return true;
      }
    ),
});

export const registrationSchema = Yup.object().shape({
  name: Yup.string()
    .min(2, TEXTS.registration.name)
    .max(30, TEXTS.registration.name)
    .matches(LETTERS_WITH_SPECIAL_CHARACTERS, TEXTS.registration.name)
    .required('Debés ingresar tu nombre.'),
  lastName: Yup.string()
    .min(2, TEXTS.registration.lastName)
    .max(30, TEXTS.registration.lastName)
    .matches(LETTERS_WITH_SPECIAL_CHARACTERS, TEXTS.registration.lastName)
    .required('Debés ingresar tu apellido.'),
  documentType: Yup.object().when(
    'docArg',
    (docArg: Optional<ValuesOf<typeof ARG_OR_EXT>[]>) => {
      const docOrArgValue = docArg?.[0];
      const isArg = docOrArgValue === ARG_OR_EXT.arg;
      if (isArg) {
        return Yup.object().required('Debés ingresar tu documento.');
      }

      return Yup.object().nullable();
    }
  ),
  docNumber: Yup.string().when(
    'docArg',
    (docArg: Optional<ValuesOf<typeof ARG_OR_EXT>[]>) => {
      const docOrArgValue = docArg?.[0];
      const isArg = docOrArgValue === ARG_OR_EXT.arg;
      return Yup.string()
        .min(2, TEXTS.registration.docNumber)
        .max(isArg ? 9 : 30)
        .required('Debés ingresar tu documento.');
    }
  ),
  birthDate: Yup.string().required('Debés ingresar tu fecha de nacimiento.'),
  gender: Yup.string().required('Debés seleccionar el sexo.'),
  supportedClub: Yup.object().required(
    'Debés ingresar el nombre del club del cual sos hincha.'
  ),
  customClubName: Yup.string().when(
    'supportedClub',
    (
      supportedClub: Optional<MakeOptional<SelectValue, 'value' | 'label'>[]>
    ) => {
      const supportTeam = supportedClub?.[0]?.value === -1;
      if (supportTeam) {
        return Yup.string()
          .min(2, TEXTS.clubName.length)
          .max(40, TEXTS.clubName.length)
          .required('Debés ingresar el nombre de tu equipo.');
      }

      return Yup.string()
        .min(2, TEXTS.clubName.length)
        .max(40, TEXTS.clubName.length);
    }
  ),
  country: Yup.object().when(
    'docArg',
    (docArg: Optional<ValuesOf<typeof ARG_OR_EXT>[]>) => {
      const docOrArgValue = docArg?.[0];
      const isArg = docOrArgValue === ARG_OR_EXT.arg;
      if (isArg) {
        return Yup.object().nullable();
      }

      return Yup.object().required('Debés seleccionar un país.');
    }
  ),
  termsConditions: Yup.boolean().oneOf(
    [true],
    'Debés aceptar los términos y condiciones del concurso.'
  ),
});

export const schoolTourmantSchema = Yup.object().shape({
  playTde: Yup.boolean(),
  isPrimary: Yup.boolean(),
  province: Yup.object().when('playTde', (playTde: Optional<boolean>[]) => {
    const isPlayTde = playTde?.pop();
    if (isPlayTde) {
      return Yup.object().required('Debés seleccionar una provincia.');
    }

    return Yup.object().nullable();
  }),
  schools: Yup.object().when('playTde', (playTde: Optional<boolean>[]) => {
    const isPlayTde = playTde?.pop();
    if (isPlayTde) {
      return Yup.object().required('Debés seleccionar una escuela.');
    }

    return Yup.object().nullable();
  }),
  location: Yup.object().when('playTde', (playTde: Optional<boolean>[]) => {
    const isPlayTde = playTde?.pop();
    if (isPlayTde) {
      return Yup.object().required('Debés seleccionar una localidad/barrio.');
    }

    return Yup.object().nullable();
  }),
  party: Yup.object().when('playTde', (playTde: Optional<boolean>[]) => {
    const isPlayTde = playTde?.pop();
    if (isPlayTde) {
      return Yup.object().required('Debés seleccionar un partido.');
    }

    return Yup.object().nullable();
  }),
});

export const enterPinTeam11 = Yup.object().shape({
  pin: Yup.string()
    .required('Ingresá un pin.')
    .matches(ONLY_LETTERS_AND_NUMBERS, TEXTS.pins.invalid)
    .max(12, TEXTS.pins.maxlenght)
    .trim(),
});

export const transferBalanceSchema = Yup.object().shape({
  amount: Yup.string()
    .required('Ingresá un monto.')
    .max(9, TEXTS.pins.maxlenght)
    .trim(),
  docNumber: Yup.string()
    .required('Ingresá un documento.')
    .min(2, TEXTS.miniumAllowed2Characters)
    .max(11, TEXTS.pins.maxlenght)
    .trim(),
  selectedReason: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.number().required(),
    })
    .required('Debés seleccionar un motivo.'),
  txtOtherReason: Yup.string().when(
    'selectedReason',
    (selectedReason: Optional<SelectValue[]>) => {
      const isOther = selectedReason?.[0]?.value === 3;
      if (isOther) {
        return Yup.string().max(20, TEXTS.pins.maxlenght).trim();
      }

      return Yup.string().nullable();
    }
  ),
});

export const faqFormSchema = Yup.object().shape({
  nameAndSurName: Yup.string()
    .min(2, 'El nombre y apellido debe tener al menos 2 caracteres.')
    .max(30, 'El nombre y apellido no puede superar los 30 caracteres.')
    .matches(
      LETTERS_WITH_SPECIAL_CHARACTERS,
      'Solo se permiten letras en este campo.'
    )
    .required('Por favor, indicá tu nombre y apellido.')
    .trim(),
  email: Yup.string()
    .email('El e-mail es inválido. Por favor, ingresalo nuevamente.')
    .matches(
      REGEX_EMAIL,
      'El e-mail es inválido. Por favor, ingresalo nuevamente.'
    )
    .required(
      'Por favor, indicá el e-mail con el que estás jugando a Gran DT.'
    ),
  category: Yup.object()
    .shape({
      label: Yup.string().required(),
      value: Yup.number().required(),
    })
    .required('Por favor, seleccioná una categoría para continuar.'),
  subcategory: Yup.object().required(
    'Por favor, seleccioná una consulta para continuar.'
  ),
  comentario: Yup.string().when('subcategory', {
    is: (subcategory: any) => subcategory && subcategory.accion === 'MENSAJE',
    then: (schema) =>
      schema
        .required(
          'Por favor, dejanos un mensaje y detallanos cuál es tu problema.'
        )
        .trim(),
    otherwise: (schema) => schema.nullable(),
  }),
  blob: Yup.mixed().when('subcategory', {
    is: (subcategory: any) => subcategory && subcategory.accion === 'MENSAJE',
    then: (schema) => schema.nullable(), // Made blob optional
    otherwise: (schema) => schema.nullable(),
  }), // Optional: Add more specific validation if needed for file type/size
});
